import type { Profile } from '@bookclub/shared';
import { MiniappSurface } from '@/shared/components/ui/MiniappSurface';
import { StatusBadge } from '@/shared/components/club/StatusBadge';

interface ProfileHeroProps {
  profile: Profile;
  onEditName?: () => void;
}

function RoleBadge({ role }: { role: Profile['role'] }) {
  if (role === 'user') return null;

  if (role === 'inhound') {
    return (
      <StatusBadge
        label="Inhound"
        tone="success"
        size="md"
        className="text-[11px] tracking-[0.08em]"
      />
    );
  }

  return (
    <StatusBadge
      status={role}
      size="md"
      className="text-[11px] tracking-[0.08em]"
    />
  );
}

export function ProfileHero({ profile, onEditName }: ProfileHeroProps) {
  const displayName =
    [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
    profile.username ||
    profile.primaryEmail ||
    profile.telegramUserId ||
    'Пользователь';
  const secondaryHint = profile.username ? `@${profile.username}` : profile.primaryEmail;

  return (
    <MiniappSurface className="overflow-hidden p-0">
      <div className="flex items-start gap-3 p-4 min-[430px]:gap-3.5 min-[430px]:p-5">
        {profile.photoUrl ? (
          <img
            src={profile.photoUrl}
            alt={displayName}
            className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-[rgba(107,78,230,0.18)] min-[430px]:h-[72px] min-[430px]:w-[72px]"
          />
        ) : (
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[rgba(107,78,230,0.12)] text-[26px] font-black text-[var(--primary)] ring-2 ring-[rgba(107,78,230,0.16)] min-[430px]:h-[72px] min-[430px]:w-[72px] min-[430px]:text-[30px]">
            {(profile.firstName?.[0] ?? profile.username?.[0] ?? '?').toUpperCase()}
          </div>
        )}

        <div className="min-w-0 flex-1 space-y-1.5 pt-0.5">
          <div className="min-w-0 space-y-1">
            <div className="flex items-start gap-2">
              <h2 className="min-w-0 flex-1 truncate text-[18px] font-black uppercase leading-[1.02] tracking-[0.02em] text-[var(--foreground)] min-[430px]:text-[20px]">
                {displayName}
              </h2>
              {onEditName ? (
                <button
                  type="button"
                  onClick={onEditName}
                  aria-label="Редактировать имя"
                  className="mt-[1px] inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[rgba(107,78,230,0.18)] bg-[rgba(107,78,230,0.08)] text-[var(--primary)] transition-colors active:bg-[rgba(107,78,230,0.14)]"
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 20 20"
                    className="h-4 w-4"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.917 3.27a1.875 1.875 0 0 1 2.652 2.651l-7.49 7.49-3.196.545.545-3.197 7.49-7.49Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="m11.667 4.52 2.812 2.812"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : null}
            </div>
            {secondaryHint ? (
              <p className="truncate text-[13px] font-medium text-[rgba(26,26,26,0.56)] min-[430px]:text-[14px]">
                {secondaryHint}
              </p>
            ) : null}
          </div>

          <RoleBadge role={profile.role} />
        </div>
      </div>
    </MiniappSurface>
  );
}
